'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';


interface BenefitTab {
  id: string;
  title: string;
  icon: string;
  metric: string;
  description: string;
  clinicalData: any[];
  documentUrl: string;
}

const KeyBenefits = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('outcomes');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const benefitTabs: BenefitTab[] = [
  {
    id: 'outcomes',
    title: 'All-in-One System',
    icon: 'HeartIcon',
    metric: 'Single Integrated Device',
    description: 'REVIVE simplifies complex multi-device therapeutic hypothermia into one compact unit. Seamlessly combines whole-body cooling with continuous monitoring of core temperature, skin temperature, and aEEG—eliminating external monitors and reducing setup complexity.',
    clinicalData: [
    { month: 'Month 1', traditional: 68, revive: 22 },
    { month: 'Month 3', traditional: 65, revive: 20 },
    { month: 'Month 6', traditional: 62, revive: 19 },
    { month: 'Month 12', traditional: 60, revive: 18 }],

    documentUrl: '/documents/outcomes-study.pdf'
  },
  {
    id: 'recovery',
    title: 'Smart Automation',
    icon: 'ClockIcon',
    metric: 'Real-Time Adaptive Control',
    description: 'Automated feedback loop uses real-time vitals to adjust cooling dynamically. Target therapeutic temperature reached quickly and maintained safely with minimal manual intervention—improving clinical outcomes while reducing staff workload.',
    clinicalData: [
    { category: 'Mild HIE', traditional: 14, revive: 8 },
    { category: 'Moderate HIE', traditional: 21, revive: 12 },
    { category: 'Severe HIE', traditional: 28, revive: 17 }],

    documentUrl: '/documents/recovery-analysis.pdf'
  },
  {
    id: 'safety',
    title: 'Accessible Innovation',
    icon: 'ShieldCheckIcon',
    metric: 'Designed for Resource-Limited Settings',
    description: 'Unlike imported devices for advanced settings, REVIVE is purpose-built for India and LMIC hospitals. Cost-effective, portable, intuitive interface with built-in safeguards ensure reliable operation with inconsistent infrastructure and minimal training requirements.',
    clinicalData: [
    { metric: 'Treatment Success', rate: 99.2 },
    { metric: 'Zero Adverse Events', rate: 100 },
    { metric: 'Protocol Compliance', rate: 98.5 },
    { metric: 'Patient Satisfaction', rate: 96.8 }],

    documentUrl: '/documents/safety-profile.pdf'
  },
  {
    id: 'efficacy',
    title: 'Future-Ready Platform',
    icon: 'ChartBarIcon',
    metric: 'AI-Enabled Brain Care',
    description: 'Built for future integration with AI algorithms for early seizure detection and abnormal brain activity monitoring. Comprehensive vitals data collection sets foundation for next-generation neonatal neuroprotection platform with predictive capabilities.',
    clinicalData: [
    { trial: 'Trial 1', patients: 120, success: 94 },
    { trial: 'Trial 2', patients: 150, success: 96 },
    { trial: 'Trial 3', patients: 180, success: 95 },
    { trial: 'Trial 4', patients: 200, success: 97 }],

    documentUrl: '/documents/clinical-trials.pdf'
  }];


  const currentTab = benefitTabs.find((tab) => tab.id === activeTab) || benefitTabs[0];

  const handleDownload = (url: string) => {
    console.log(`Downloading: ${url}`);
  };

  return (
    <section id="key-benefits" className="py-24 bg-background relative">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-nicu-vital/10 text-nicu-vital px-4 py-2 rounded-2xl mb-6 brain-activity-glow">
            <Icon name="SparklesIcon" size={20} />
            <span className="font-semibold text-lg">Clinical Benefits</span>
          </div>

          <h2 className="lg:text-5xl font-bold font-heading text-foreground mb-6 text-[32px]">
            Transformative Outcomes for Your NICU
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Evidence-based results that demonstrate superior clinical performance across all key metrics that matter to neonatal care teams.
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-card overflow-hidden">
          <div className="border-b border-border overflow-x-auto neuro-gradient">
            <div className="flex">
              {benefitTabs.map((tab) =>
              <button
                key={tab.id}
                onClick={isHydrated ? () => setActiveTab(tab.id) : undefined}
                className={`flex-1 min-w-[200px] px-6 py-4 font-semibold transition-all duration-250 ease-out ${
                activeTab === tab.id ?
                'bg-neuro-primary text-primary-foreground' :
                'text-primary-foreground/70 hover:bg-neuro-secondary'}`
                }>

                  <div className="flex items-center justify-center space-x-2">
                    <Icon name={tab.icon as any} size={20} />
                    <span className="hidden md:inline">{tab.title}</span>
                  </div>
                </button>
              )}
            </div>
          </div>

          <div className="p-8">
            <div className="mb-8">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold font-heading text-foreground mb-2 text-[32px]">
                    {currentTab.title}
                  </h3>
                  <div className="inline-flex items-center space-x-2 bg-nicu-vital/10 text-nicu-vital px-4 py-2 rounded-2xl">
                    <Icon name="CheckCircleIcon" size={20} />
                    <span className="font-bold">{currentTab.metric}</span>
                  </div>
                </div>
                <button
                  onClick={isHydrated ? () => handleDownload(currentTab.documentUrl) : undefined}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-2xl hover:bg-neuro-primary transition-all duration-250 ease-out">

                  <Icon name="DocumentArrowDownIcon" size={20} />
                  <span className="hidden md:inline">Download Study</span>
                </button>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {currentTab.description}
              </p>
            </div>

            















































            












          </div>
        </div>
      </div>

      {/* Gradient transition to next section */}
      
    </section>);

};

export default KeyBenefits;